import type { Question } from 'src/redux/survey/survey.types';
import type {
  AnswerBucketMapping,
  LinkedQuestionGroup,
  PerSurveyMapping,
  SurveyGroup,
} from 'src/types/survey-group.types';

export interface QuestionMapState {
  /** anchorQuestionId -> memberQuestionId | null */
  byAnchorQuestionId: Record<string, string | null>;
}

export interface AnswerMapState {
  /** key `${anchorQId}__${anchorAId}` -> member answer id + optional label */
  byKey: Record<string, { memberAnswerId: string | null; displayLabelOverride?: string }>;
}

function buildAnswerBuckets(
  anchorQuestion: Question,
  memberQuestion: Question | undefined,
  answerState: AnswerMapState
): AnswerBucketMapping[] {
  if (!memberQuestion) {
    return anchorQuestion.answers.map((aa) => ({
      anchorAnswerId: aa.id,
      memberAnswerId: null,
    }));
  }
  return anchorQuestion.answers.map((aa) => {
    const key = `${anchorQuestion.id}__${aa.id}`;
    const row = answerState.byKey[key];
    return {
      anchorAnswerId: aa.id,
      memberAnswerId: row?.memberAnswerId ?? null,
      displayLabelOverride: row?.displayLabelOverride,
    };
  });
}

export function buildPerSurveyMapping(
  anchorQuestions: Question[],
  memberQuestions: Question[],
  memberExtSurveyId: string,
  qMap: QuestionMapState,
  answerMaps: Record<string, AnswerMapState>
): PerSurveyMapping {
  const memberById = new Map(memberQuestions.map((q) => [q.id, q]));
  const linkedQuestions: LinkedQuestionGroup[] = [];

  for (const aq of anchorQuestions) {
    const memberQId = qMap.byAnchorQuestionId[aq.id] ?? null;
    const mq = memberQId ? memberById.get(memberQId) : undefined;
    const answerState = answerMaps[memberExtSurveyId] ?? { byKey: {} };
    linkedQuestions.push({
      anchorQuestionId: aq.id,
      memberQuestionId: memberQId,
      answerBuckets: buildAnswerBuckets(aq, mq, answerState),
    });
  }

  return {
    extSurveyId: memberExtSurveyId,
    linkedQuestions,
  };
}

export function createSurveyGroupDraft(params: {
  name: string;
  anchorExtSurveyId: string;
  memberExtSurveyIds: string[];
  mappings: PerSurveyMapping[];
}): SurveyGroup {
  const now = new Date().toISOString();
  return {
    id: typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `sg_${Date.now()}`,
    name: params.name,
    createdAt: now,
    updatedAt: now,
    anchorExtSurveyId: params.anchorExtSurveyId,
    memberExtSurveyIds: params.memberExtSurveyIds,
    mappings: params.mappings,
  };
}
